import { useAppShell } from "@foundation-tool/ui/hooks";
import { Countries } from "@foundation-tool/ui/types";
import { parseQueryString } from "@foundation-tool/ui/utilities";
import { navigate } from "gatsby";
import { useEffect } from "react";
import { PATHS, sanitizeUrl } from "../utils/route";

// --- EC campaign landing (temporary) — delete this file + onRouteUpdate in gatsby-browser ---
// Applies to Ecuador (ec) only.

const CAMPAIGN_CODES = ["202612", "202613", "202614"];
const ECUADOR = Countries.EC;

function isEmbedded(): boolean {
  return typeof window !== "undefined" && window.self !== window.top;
}

function isDirectFeatureEnabled(): boolean {
  return process.env.GATSBY_EC_CAMPAIGN_LANDING_DIRECT_ENABLED === "true";
}

function isVtexFeatureEnabled(): boolean {
  return process.env.GATSBY_EC_CAMPAIGN_LANDING_VTEX_ENABLED === "true";
}

function isCatalogFeatureEnabled(): boolean {
  return process.env.GATSBY_EC_CAMPAIGN_LANDING_CATALOG_ENABLED === "true";
}

function isCatalogOrigin(vieneDe = ""): boolean {
  const normalized = vieneDe.toLowerCase();
  return normalized.startsWith("catalogo_") || normalized === "catalogo";
}

function isFeatureEnabledForContext(vieneDe = ""): boolean {
  if (isEmbedded()) {
    if (isVtexOrigin(vieneDe)) return isVtexFeatureEnabled();
    if (isCatalogOrigin(vieneDe)) return isCatalogFeatureEnabled();
    return false;
  }

  return isDirectFeatureEnabled();
}

function resolveQueryParam(
  key: string,
  valueFromStore = "",
): string {
  if (typeof window !== "undefined") {
    const queryValue = parseQueryString(window.location.search)[key];
    if (queryValue) return String(queryValue);
  }
  return valueFromStore;
}

function resolveCampaignCode(codCampanaFromStore = ""): string {
  return resolveQueryParam("cod_campana", codCampanaFromStore);
}

function resolveCodCountry(codCountryFromStore = ""): string {
  return resolveQueryParam("cod_country", codCountryFromStore);
}

function resolveVieneDe(vieneDeFromStore = ""): string {
  return resolveQueryParam("vieneDe", vieneDeFromStore);
}

function isVtexOrigin(vieneDe = ""): boolean {
  const normalized = vieneDe.toLowerCase();
  return (
    normalized === "vtex_ecom" ||
    normalized.startsWith("vtex_mto") ||
    normalized.startsWith("vtex")
  );
}

function isCampaignCode(code: string): boolean {
  const normalized = code.trim();
  if (!normalized) return false;
  return CAMPAIGN_CODES.some(
    (c) => normalized === c || normalized.startsWith(`${c}-`),
  );
}

/** Route and optional iframe param must both be Ecuador when cod_country is present. */
function isEcuadorContext(
  pathCountry: Countries,
  codCountry = "",
): boolean {
  if (pathCountry !== ECUADOR) return false;

  const normalizedCodCountry = codCountry.trim().toLowerCase();
  if (!normalizedCodCountry) return true;

  return normalizedCodCountry === ECUADOR;
}

function isCampaignOnlyMode(
  pathCountry: Countries,
  campaignCode: string,
  codCountry = "",
  vieneDe = "",
): boolean {
  if (!isEcuadorContext(pathCountry, codCountry)) return false;
  if (!isFeatureEnabledForContext(vieneDe)) return false;

  if (isEmbedded()) {
    if (isVtexOrigin(vieneDe)) return true;
    if (isCatalogOrigin(vieneDe)) return isCampaignCode(campaignCode);
    return false;
  }

  return true;
}

function getCountryFromPathname(pathname: string): Countries | null {
  const match = pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  if (!match) return null;
  const iso = match[1];
  return Object.values(Countries).includes(iso as Countries)
    ? (iso as Countries)
    : null;
}

function isCountryHome(pathname: string, countryIso: Countries): boolean {
  const normalized = pathname.replace(/\/$/, "") || "/";
  return normalized === `/${countryIso}`;
}

export function redirectEcCampaignLandingIfNeeded(
  codCampanaFromStore = "",
  codCountryFromStore = "",
  vieneDeFromStore = "",
) {
  if (typeof window === "undefined") return;

  const pathname = window.location.pathname;
  const pathCountry = getCountryFromPathname(pathname);
  if (!pathCountry) return;

  const campaignCode = resolveCampaignCode(codCampanaFromStore);
  const codCountry = resolveCodCountry(codCountryFromStore);
  const vieneDe = resolveVieneDe(vieneDeFromStore);
  if (!isCampaignOnlyMode(pathCountry, campaignCode, codCountry, vieneDe)) {
    return;
  }
  if (isCountryHome(pathname, pathCountry)) return;

  return navigate(sanitizeUrl(pathCountry, PATHS.home), { replace: true });
}

export function useEcCampaignLanding(countryIso: Countries): {
  isCampaignOnlyMode: boolean;
  campaignCode: string;
} {
  const { iframeParams } = useAppShell();
  const campaignCode = resolveCampaignCode(iframeParams.codCampana);
  const codCountry = resolveCodCountry(iframeParams.codCountry);
  const vieneDe = resolveVieneDe(iframeParams.vieneDe);

  useEffect(() => {
    redirectEcCampaignLandingIfNeeded(
      iframeParams.codCampana,
      iframeParams.codCountry,
      iframeParams.vieneDe,
    );
  }, [
    countryIso,
    campaignCode,
    codCountry,
    vieneDe,
    iframeParams.codCampana,
    iframeParams.codCountry,
    iframeParams.vieneDe,
  ]);

  return {
    isCampaignOnlyMode: isCampaignOnlyMode(
      countryIso,
      campaignCode,
      codCountry,
      vieneDe,
    ),
    campaignCode,
  };
}
